/**
 * Glitch — the twelve-frame schedule, and the damage one frame applies.
 *
 * `data/glitch.ts` authors *how much* a level breaks. This decides *when* and *where*,
 * and then does it. Two things are kept apart on purpose.
 *
 * **The schedule is data.** A `Schedule` is a few dozen small integers: which frames are
 * hot, which rows displace, by how much, which frames flash. It is small enough to emit
 * into the animated HTML and let the browser do the work — which is the only way a
 * twelve-frame loop fits in a data URI at all, one grid of 1024 bytes and a schedule
 * rather than twelve grids. `applyFrame` is the same transform in TypeScript, so the CLI
 * can render any frame to PNG and a parity fixture can compare one frame byte for byte
 * against Solidity.
 *
 * **Nothing here holds an RNG.** Every decision is a `hash32` addressed by (frame, what),
 * so frame 9's third band can be recomputed without replaying frames 0..8. The Solidity
 * mirror needs exactly that, because it renders one frame per call, and it is the same
 * positional addressing the terrain field and the plumage dither already use.
 */

import { ELEVATION_LEVELS, GLITCH_FRAMES, GRID_COLS, GRID_ROWS, LOOP_MS } from "../constants.js";
import { hash32, iabs, idiv, shr, umod } from "../engine/int.js";
import type { Grid } from "./cell.js";
import {
  GLYPH_BLANK,
  cellGlyph,
  cellIndex,
  cellSlot,
  get,
  newGrid,
  packCell,
} from "./cell.js";
import { SLOT_ACCENT, SLOT_BG, SLOT_SPARK, SLOT_TERRAIN } from "./palette.js";
import { GLITCH_LEVELS } from "../data/glitch.js";
import type { GlitchLevel } from "../data/glitch.js";

/**
 * The frame the still image is taken from, and the one frame that is never hot.
 *
 * `validateGlitch` already guarantees *a* clean frame exists; reserving index 0 is the
 * stronger choice and it buys two things. `tokenURI` renders the still without
 * simulating the loop to find a clean frame, and the loop restarts on the whole bird —
 * a stepped twelve-frame second then reads as damage and recovery rather than as
 * continuous noise, which is the difference between a glitch and a screensaver.
 */
export const STILL_FRAME = 0;

/**
 * The two colour slots a flicker frame exchanges.
 *
 * The terrain ramp's two ends, and not `ink` and `bg`, because of a measurement:
 * `composecheck` reports zero empty cells on every one of 1024 composed tokens, so
 * `SLOT_BG` paints nothing at all in a still and swapping it would be a flash nobody
 * sees. The frame is entirely land, so the slots most of it is painted with are the
 * ramp's ends; exchanging them inverts the landscape's tone for exactly one frame while
 * leaving every glyph — and therefore the whole silhouette — exactly where it was.
 */
export const FLICKER_SLOT_A = SLOT_TERRAIN;
export const FLICKER_SLOT_B = SLOT_TERRAIN + ELEVATION_LEVELS - 1;

/**
 * The chroma split: two ghost passes of the whole frame, drawn before the real one.
 *
 * A hot frame's `chroma` is an offset in cells; the frame is painted three times, once
 * shifted `-chroma` in `GHOST_SLOT_LEFT`, once `+chroma` in `GHOST_SLOT_RIGHT`, and then
 * normally on top. `GHOST_ALPHA` is what the two ghosts are drawn at, so a ghost tints
 * whatever is under it instead of replacing it — the bird stays legible while its edges
 * separate into colour, which is what a chromatic aberration is.
 *
 * The two slots are the palette's two loudest, and neither is a slot the frame is *made*
 * of: `SLOT_ACCENT` and `SLOT_SPARK` are trim, so a ghost cannot be mistaken for the
 * plumage or the terrain sliding. `FLICKER_SLOT_A`/`_B` are deliberately not among them —
 * a flicker frame that also splits would otherwise swap its own ghosts.
 *
 * These three numbers live here because nothing renders them except the animation, and the
 * animation has no reference renderer: `STILL_FRAME` is never hot, so the SVG and the PNG
 * never draw a ghost at all. The payload's player draws them, `emit/glsl.ts` draws them
 * again on the GPU, and two mirrors with nothing between them is precisely the drift
 * `tools/playercheck.ts` exists to catch — so it asserts these values rather than the
 * literals it used to.
 */
export const GHOST_ALPHA = 0.34;
export const GHOST_SLOT_LEFT = SLOT_SPARK;
export const GHOST_SLOT_RIGHT = SLOT_ACCENT;

/**
 * Draw addresses, fixed forever: the Solidity mirror hashes the same pairs.
 *
 * `DRAW_SLICE` is a base that a band index is added to, so it must stay far enough below
 * the next address to hold the widest level authored — seven bands, and room to grow.
 */
const DRAW_HOT = 0x10;
const DRAW_SLICE = 0x20;
const DRAW_FLICKER = 0x30;
const DRAW_CORRUPT = 0x40;

/** One displaced horizontal band: rows `y0..y1` inclusive, moved `shift` cells. */
export interface Slice {
  readonly y0: number;
  readonly y1: number;
  /** Signed cells, never zero — a band that does not move is a band wasted. */
  readonly shift: number;
}

export interface Frame {
  readonly index: number;
  readonly hot: boolean;
  /** Empty on a clean frame, `level.sliceBands` long on a hot one. */
  readonly slices: readonly Slice[];
  /** Chroma ghost offset in cells: the level's on a hot frame, 0 on a clean one. */
  readonly chroma: number;
  readonly flicker: boolean;
  /** Seed for the per-cell corruption hash. Zero on a clean frame, which never corrupts. */
  readonly corruptSeed: number;
}

export interface Schedule {
  readonly frames: readonly Frame[];
  /** Hot frame indices, ascending. Exactly `level.hotFrames` of them. */
  readonly hot: readonly number[];
  readonly still: number;
  /** The level's per-cell corruption chance in 1/256, carried through for `applyFrame`. */
  readonly corrupt: number;
  readonly durationMs: number;
}

/**
 * Rows of band `band` when the grid is cut into `bands` of them.
 *
 * Bresenham boundaries — `band * GRID_ROWS / bands` — so the bands tile the grid exactly
 * with the remainder spread through them instead of dumped on the last one. At the
 * authored maximum of seven that gives heights 4,5,4,5,4,5,5: every band clears the four
 * rows `validateGlitch` requires for a band to still read as a band rather than as dither.
 *
 * The bands tile rather than float because that is what the authored data means. A level
 * says how many bands move and how far, and nothing about where they are — so "three
 * bands" is a grid cut in three, all of it moving, which is why Stable's single band is
 * the whole frame twitching by one cell and Collapse's seven are a shred.
 */
export function bandRows(bands: number, band: number): { y0: number; y1: number } {
  return {
    y0: idiv(band * GRID_ROWS, bands),
    y1: idiv((band + 1) * GRID_ROWS, bands) - 1,
  };
}

/**
 * Which frames are hot: `level.hotFrames` distinct indices, never `STILL_FRAME`.
 *
 * A partial Fisher-Yates over the eleven candidates, which is exact — no rejection loop,
 * and a frame cannot be drawn twice. Rolling a chance per frame instead would turn
 * `hotFrames` from a count into an average, and a Collapse token that rolled zero hot
 * frames would be a Collapse that never collapses.
 */
export function hotFrames(level: GlitchLevel, seed: number): number[] {
  const pool: number[] = [];
  for (let f = 0; f < GLITCH_FRAMES; f++) if (f !== STILL_FRAME) pool.push(f);
  if (level.hotFrames > pool.length) {
    throw new Error(`${level.name}: ${level.hotFrames} hot frames of ${pool.length} candidates`);
  }
  for (let i = 0; i < level.hotFrames; i++) {
    const j = i + umod(hash32(i, DRAW_HOT, seed), pool.length - i);
    const swap = pool[i] as number;
    pool[i] = pool[j] as number;
    pool[j] = swap;
  }

  // Back to ascending order through a flag array rather than a sort: the emitter wants
  // the frames in loop order, and a twelve-bit flag array is what Solidity would build.
  const hot = new Array<boolean>(GLITCH_FRAMES).fill(false);
  for (let i = 0; i < level.hotFrames; i++) hot[pool[i] as number] = true;
  const out: number[] = [];
  for (let f = 0; f < GLITCH_FRAMES; f++) if (hot[f] === true) out.push(f);
  return out;
}

/**
 * One token's whole loop, from its level and its `frameSeed`.
 *
 * Every hot frame draws one 32-bit word per band and splits it: the low bit is the
 * direction and the rest is the magnitude. One draw rather than two, because a sign is
 * one bit and spending a whole hash on it would double the Solidity mirror's keccak
 * count for nothing. The magnitude is `1 + w % maxSlice`, so it is never zero — the level
 * already said this band moves, and a zero would silently make a hot frame clean.
 */
export function buildSchedule(level: GlitchLevel, frameSeed: number): Schedule {
  const hot = hotFrames(level, frameSeed);
  const isHot = new Array<boolean>(GLITCH_FRAMES).fill(false);
  for (const f of hot) isHot[f] = true;

  const frames: Frame[] = [];
  for (let index = 0; index < GLITCH_FRAMES; index++) {
    if (isHot[index] !== true) {
      frames.push({ index, hot: false, slices: [], chroma: 0, flicker: false, corruptSeed: 0 });
      continue;
    }
    const slices: Slice[] = [];
    for (let band = 0; band < level.sliceBands; band++) {
      const w = hash32(index, DRAW_SLICE + band, frameSeed);
      const mag = 1 + umod(shr(w, 1), level.maxSlice);
      const rows = bandRows(level.sliceBands, band);
      slices.push({ y0: rows.y0, y1: rows.y1, shift: (w & 1) === 0 ? mag : -mag });
    }
    frames.push({
      index,
      hot: true,
      slices,
      chroma: level.chroma,
      flicker: umod(hash32(index, DRAW_FLICKER, frameSeed), 256) < level.flicker,
      corruptSeed: hash32(index, DRAW_CORRUPT, frameSeed),
    });
  }

  return { frames, hot, still: STILL_FRAME, corrupt: level.corrupt, durationMs: LOOP_MS };
}

/**
 * When frame `i` starts, in milliseconds from the top of the loop.
 *
 * Integer division of the whole second, so the twelve starts are 0, 83, 166, 250, 333,
 * 416, 500, 583, 666, 750, 833, 916 and `frameStartMs(GLITCH_FRAMES)` is exactly
 * `LOOP_MS`. The brief says one second and 1000 does not divide by 12, so the emitter
 * reads *boundaries* from here rather than adding up twelve rounded durations — which
 * would drift the loop off the second by up to four milliseconds a cycle.
 */
export function frameStartMs(frame: number): number {
  return idiv(frame * LOOP_MS, GLITCH_FRAMES);
}

/**
 * One frame's grid: the still displaced band by band, corrupted in place.
 *
 * A *gather* — each output cell reads the cell `shift` to its left — because that makes
 * both edges of a displacement one expression instead of two passes. `get` returns the
 * empty cell off-grid, so the cells a band vacates come back as background and the cells
 * it pushes past the edge are simply never read: the same dropout `put` gives a write.
 * Background is the only thing a token ever shows of `SLOT_BG`, since a composed still
 * has no empty cell anywhere in it.
 *
 * Corruption is addressed by the cell's *final* position, so the grain of the damage
 * stays nailed to the frame while ink slides through it — the same reason the plumage
 * dither is positional. It replaces the glyph and keeps the colour slot, so a corrupted
 * cell reads as noise in the surface of the thing it belongs to rather than as a stray
 * mark from somewhere else, and it can never name a colour the palette did not author.
 * Blank cells are skipped: there is no ink in a gap to corrupt, and filling the gap back
 * in would erase the displacement's only visible edge.
 */
export function applyFrame(base: Grid, frame: Frame, corrupt: number): Grid {
  const out = newGrid();
  out.set(base);
  if (!frame.hot) return out;

  for (const s of frame.slices) {
    for (let y = s.y0; y <= s.y1; y++) {
      for (let x = 0; x < GRID_COLS; x++) {
        let cell = get(base, x - s.shift, y);
        if (corrupt > 0 && cellGlyph(cell) !== GLYPH_BLANK) {
          const w = hash32(x, y, frame.corruptSeed);
          if (umod(w, 256) < corrupt) {
            cell = packCell(umod(shr(w, 8), ELEVATION_LEVELS), cellSlot(cell));
          }
        }
        out[cellIndex(x, y)] = cell;
      }
    }
  }
  return out;
}

/** Structural guard invoked by tests. */
export function validateSchedule(): void {
  if (STILL_FRAME < 0 || STILL_FRAME >= GLITCH_FRAMES) throw new Error("the still frame is off the loop");
  if (FLICKER_SLOT_A === FLICKER_SLOT_B) throw new Error("a flicker that swaps a slot with itself is invisible");

  if (GHOST_ALPHA <= 0 || GHOST_ALPHA >= 1) throw new Error(`a ghost at alpha ${GHOST_ALPHA} is not a ghost`);
  // Through an array rather than a `===`, which the compiler rejects as dead code while the
  // two slots are different literals — and would stop rejecting the moment they were not.
  const ghosts: number[] = [GHOST_SLOT_LEFT, GHOST_SLOT_RIGHT];
  if (new Set(ghosts).size !== ghosts.length) throw new Error("both ghosts are the same colour, so there is no split");
  for (const slot of ghosts) {
    if (slot === SLOT_BG) throw new Error("a ghost drawn in the background colour is invisible");
    if (slot === FLICKER_SLOT_A || slot === FLICKER_SLOT_B) {
      throw new Error(`ghost slot ${slot} is a flicker slot, so a flicker frame would swap its own ghosts`);
    }
  }

  if (frameStartMs(0) !== 0) throw new Error("the loop does not start at zero");
  if (frameStartMs(GLITCH_FRAMES) !== LOOP_MS) {
    throw new Error(`the ${GLITCH_FRAMES} frames end at ${frameStartMs(GLITCH_FRAMES)}ms, not ${LOOP_MS}`);
  }
  for (let f = 1; f <= GLITCH_FRAMES; f++) {
    if (frameStartMs(f) <= frameStartMs(f - 1)) throw new Error(`frame ${f} starts before frame ${f - 1}`);
  }

  // Bands must tile the grid for every count a level may author, or a displacement would
  // leave a seam of undisplaced rows that reads as a bug rather than as a tear.
  for (let bands = 1; bands * 4 <= GRID_ROWS; bands++) {
    let next = 0;
    for (let band = 0; band < bands; band++) {
      const { y0, y1 } = bandRows(bands, band);
      if (y0 !== next) throw new Error(`${bands} bands: band ${band} starts at ${y0}, not ${next}`);
      if (y1 < y0 + 3) throw new Error(`${bands} bands: band ${band} is ${y1 - y0 + 1} rows, under four`);
      next = y1 + 1;
    }
    if (next !== GRID_ROWS) throw new Error(`${bands} bands cover ${next} rows of ${GRID_ROWS}`);
  }

  for (const level of GLITCH_LEVELS) {
    for (const seed of [0, 1, 0x5a5a5a5a, 0xdeadbeef, 0xffffffff]) {
      const s = buildSchedule(level, seed);
      if (s.frames.length !== GLITCH_FRAMES) throw new Error(`${level.name}: ${s.frames.length} frames`);
      if (s.hot.length !== level.hotFrames) {
        throw new Error(`${level.name}: ${s.hot.length} hot frames, not ${level.hotFrames}`);
      }
      if (new Set(s.hot).size !== s.hot.length) throw new Error(`${level.name}: a frame is hot twice`);
      if (s.hot.includes(STILL_FRAME)) throw new Error(`${level.name}: the still frame is hot`);
      for (const frame of s.frames) {
        if (frame.hot !== s.hot.includes(frame.index)) {
          throw new Error(`${level.name}: frame ${frame.index} disagrees with the hot list`);
        }
        if (!frame.hot) {
          if (frame.slices.length !== 0 || frame.chroma !== 0 || frame.flicker) {
            throw new Error(`${level.name}: a clean frame carries damage`);
          }
          continue;
        }
        if (frame.slices.length !== level.sliceBands) {
          throw new Error(`${level.name}: ${frame.slices.length} bands, not ${level.sliceBands}`);
        }
        for (const slice of frame.slices) {
          if (slice.shift === 0) throw new Error(`${level.name}: a band does not move`);
          if (iabs(slice.shift) > level.maxSlice) {
            throw new Error(`${level.name}: a band moves ${slice.shift} cells, past ${level.maxSlice}`);
          }
        }
      }
    }
  }
  validateDisplacement();
}
/**
 * What a displacement does to a full grid, checked on the loudest level.
 *
 * Split out of `validateSchedule` only for length. Two claims, both of which are the
 * whole point of `applyFrame`: a clean frame is the composed scene untouched, and a hot
 * band opens exactly as many background cells as it moved. The second is measured at
 * `corrupt = 256` — every ink cell corrupted, the worst case — because corruption
 * skipping blank cells is the only reason the dropout survives at all.
 */
function validateDisplacement(): void {
  const base = newGrid().fill(packCell(1, SLOT_TERRAIN));
  const level = GLITCH_LEVELS[GLITCH_LEVELS.length - 1] as GlitchLevel;
  const s = buildSchedule(level, 0x5a5a5a5a);

  const still = applyFrame(base, s.frames[STILL_FRAME] as Frame, level.corrupt);
  for (let i = 0; i < base.length; i++) {
    if (still[i] !== base[i]) throw new Error("the still frame is not the composed scene untouched");
  }

  for (const f of s.hot) {
    const frame = s.frames[f] as Frame;
    const out = applyFrame(base, frame, 256);
    for (const slice of frame.slices) {
      for (let y = slice.y0; y <= slice.y1; y++) {
        let blank = 0;
        for (let x = 0; x < GRID_COLS; x++) {
          if (cellGlyph(out[cellIndex(x, y)] as number) === GLYPH_BLANK) blank++;
        }
        if (blank !== iabs(slice.shift)) {
          throw new Error(
            `a ${slice.shift}-cell displacement opened ${blank} background cells in row ${y}`,
          );
        }
      }
    }
  }
}

